import difference from 'lodash/difference';
import round from 'lodash/round';
import zip from 'lodash/zip';

import {OVERALL_PROP, OVERALL_PROP_NAME, SEGMENT_SEPARATOR} from 'common/report/models/funnels/constants';
import {renamePropertyValue} from 'common/report/util/properties';
import {
  BasicFunnelStep,
  ChartType,
  DateKeyedFunnelSegments,
  FunnelSegment,
  FunnelStep,
  SegmentPropToDetailsMap,
} from 'common/report/models/funnels/types';
import {FunnelsQuery} from 'common/report/models/funnels/funnels-query';
import {
  isSegmentedData,
  KeyedByDate,
  SegmentedFunnelResults,
  UnsegmentedFunnelResult,
} from 'common/report/data/funnels/api-types';
import {collectNestedSegments, formatDateString, generateOverallSegment} from 'common/report/util/funnels/utils';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {convertSegfilterToPropertyFilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {PropertyType} from 'common/report/constants';
import {pluralize} from 'common/util/string';

export function addConversionRateToFunnelStep(funnelStep: FunnelStep): void {
  if (funnelStep.dropoff_step_freq_buckets && funnelStep.convert_step_freq_buckets) {
    funnelStep.convert_rate_freq_buckets = {
      buckets: funnelStep.dropoff_step_freq_buckets.buckets.map((_, i) => {
        return calculateAverage(
          funnelStep.convert_step_freq_buckets.buckets[i],
          funnelStep.dropoff_step_freq_buckets.buckets[i],
        );
      }),
      lower: calculateAverage(funnelStep.convert_step_freq_buckets.lower, funnelStep.dropoff_step_freq_buckets.lower),
      higher: calculateAverage(
        funnelStep.convert_step_freq_buckets.higher,
        funnelStep.dropoff_step_freq_buckets.higher,
      ),
    };

    funnelStep.convert_rate_freq_percentiles = funnelStep.dropoff_step_freq_percentiles.map((_, i) => {
      return round(
        calculateAverage(funnelStep.convert_step_freq_percentiles[i], funnelStep.dropoff_step_freq_percentiles[i]),
        2,
      );
    });
  }
}

/**
 * Calculates the convert_rate_freq_buckets and convert_rate_freq_percentiles for Funnels frequency chart
 * @param {Array<DateKeyedFunnelSegment>} segmentData - the segment that needs convertion rate properties added to it
 * @return {Array<DateKeyedFunnelSegment>} - containing convert_rate_freq_buckets and convert_rate_freq_percentiles
 */
export function calculateFunnelFrequencyConversionRate(segmentData: DateKeyedFunnelSegments[]) {
  segmentData.forEach((dateKeyFunnelSegment) => {
    dateKeyFunnelSegment.segments.forEach((funnelSegment) => {
      funnelSegment.steps.forEach((step) => {
        const funnelStep: FunnelStep = step; // have to set type because it can be a BasicFunnelStep or FunnelStep

        addConversionRateToFunnelStep(funnelStep);
      });
    });
  });

  return segmentData;
}

function calculateAverage(conversion: number, dropOff: number) {
  return conversion / (dropOff + conversion) || 0;
}

export function createFunnelDataWithRename(
  funnelData: KeyedByDate<UnsegmentedFunnelResult | SegmentedFunnelResults>,
  funnelsQuery: FunnelsQuery,
): DateKeyedFunnelSegments[] {
  const segment = Object.values(funnelData)[0];
  let segmentRenameFunc = null;
  if (isSegmentedData(segment)) {
    const segmentNames = funnelsQuery.getSegmentNames();
    const segmentTypes = funnelsQuery.getSegmentTypes();
    segmentRenameFunc = (seg) => renameSegments(seg, segmentNames, segmentTypes);
  }
  return createFunnelData(funnelData, funnelsQuery.chartType, segmentRenameFunc);
}

/**
 * Given funnelData, return DateKeyedFunnelSegments that are optionally renamed and ordered by date
 * @param funnelData Array of result data mapped by Date
 * @param renameSegmentFunc Fuction to rename segments
 */
export function createFunnelData(
  funnelData: KeyedByDate<UnsegmentedFunnelResult | SegmentedFunnelResults>,
  chartType: ChartType,
  renameSegmentFunc: (FunnelSegment) => FunnelSegment = null,
): DateKeyedFunnelSegments[] {
  let dateKeyedSegments: DateKeyedFunnelSegments[] = Object.keys(funnelData).map((date) => {
    let segments;
    const segment = funnelData[date];
    // If it is unsegmented, overall won't be keyed so we need to create the object
    if (isSegmentedData(segment)) {
      segments = collectNestedSegments(segment);
      /** We don't currently rename on dashboards so we need to pass in a rename func on Funnels */
      if (renameSegmentFunc) {
        segments = segments.map((seg) => renameSegmentFunc(seg));
      }
    } else {
      segments = [generateOverallSegment(segment.steps)];
    }
    return {
      date: new Date(formatDateString(date)),
      segments,
    };
  });
  if (chartType === ChartType.Frequency) {
    dateKeyedSegments = calculateFunnelFrequencyConversionRate(dateKeyedSegments);
  }
  const allProps = getSegmentProps(dateKeyedSegments);
  dateKeyedSegments = dateKeyedSegments.map((dateKeyedSegment) => ({
    ...dateKeyedSegment,
    segments: addEmptySegments(dateKeyedSegment.segments, allProps),
  }));
  dateKeyedSegments.sort((a, b) => a.date.getTime() - b.date.getTime());
  return dateKeyedSegments;
}

/**
 * Order Funnel Segments by highest to lowest absolute
 * number of average funnel conversions during the time period.
 * Also ensures that every date key's value has the same number of segments.
 */
export function orderSegmentsbyTotalConversions(funnelSegments: DateKeyedFunnelSegments[]): DateKeyedFunnelSegments[] {
  if (!funnelSegments || funnelSegments.length === 0) {
    return [];
  }
  const allProps = getSegmentProps(funnelSegments);
  const totalConversionsMap = getSegmentsToTotalConversionsMap(Object.keys(allProps), funnelSegments);
  return funnelSegments.map(({date, segments}) => {
    const orderedSegments = addEmptySegments(segments, allProps) //ensures all props have steps for all date values
      .sort((a, b) => {
        if (a.prop === OVERALL_PROP) {
          return -1;
        } else if (b.prop === OVERALL_PROP) {
          return 1;
        } else {
          return totalConversionsMap.get(b.prop) - totalConversionsMap.get(a.prop);
        }
      });
    return {date, segments: orderedSegments};
  });
}

/**
 * Given the segmented over a certain time range,
 * returns a SegmentPropToDetailsMap containing the complete set of props across time buckets.
 */
function getSegmentProps(funnelSegments: DateKeyedFunnelSegments[]): SegmentPropToDetailsMap {
  const propToDetailsMap = {};
  funnelSegments.forEach(({segments}) => {
    segments.forEach(({prop, propName, props}) => {
      propToDetailsMap[prop] = {propName, props};
    });
  });
  return propToDetailsMap;
}

/**
 * Given segmented data over a date range, returns a map of segment labels
 * to the sum of all numerical funnel conversions over that date range
 * @return {Map<string, number>} map of segment label to sum of funnel conversions
 */
function getSegmentsToTotalConversionsMap(
  allProps: string[],
  funnelSegments: DateKeyedFunnelSegments[],
): Map<string, number> {
  const segmentMap = new Map();
  allProps.forEach((prop) => {
    segmentMap.set(prop, 0);
  });
  funnelSegments.forEach(({segments}) => {
    segments.forEach(({prop, steps}) => {
      const currentVal = segmentMap.get(prop);
      segmentMap.set(prop, currentVal + steps[steps.length - 1].count);
    });
  });
  return segmentMap;
}

/**
 * Given an array of segments and a set of props "allProps" (a
 * superset of all the segment props),
 * returns an augmented array of segments with an entry for every
 * prop in "allProps", with potentially empty steps.
 */
function addEmptySegments(segments: FunnelSegment[], allProps: SegmentPropToDetailsMap): FunnelSegment[] {
  // Conflicted on using OVERALL_PROP here or using assumption that there will be segments that contain steps
  const emptyStepData = createBlankSteps(segments.find((segment) => segment.steps.length > 0));
  const nonNullProps = segments.map(({prop}) => prop);
  const nullProps = difference(Object.keys(allProps), nonNullProps);
  // adds funnels segments with empty steps to ensure every prop is in the final segments object
  const emptySegments = nullProps.map((prop) => {
    const {propName, props} = allProps[prop];
    return {prop, propName, props, steps: emptyStepData};
  });
  return [...segments, ...emptySegments];
}

/**
 * Given an array of date keyed segments and an array of selected segments,
 * returns an array with only the date keyed segmented whose prop values are
 * in the provided selected segments array.
 *
 * @param funnelSegments array of date keyed funnel segments
 * @param selectedSegments array of the selected segments (selected segments are represented as key to color objects)
 * @return {DateKeyedFunnelSegments[]} array of the funnel segments whose prop values are present provided selected segments array
 */
export function filterForSelectedSegments(
  funnelSegments: DateKeyedFunnelSegments[],
  selectedSegments: ColorChoice[],
): DateKeyedFunnelSegments[] {
  const selectedFunnelSegments: DateKeyedFunnelSegments[] = [];
  const selectedProps = selectedSegments.map((s) => s.key);
  funnelSegments.forEach(({date, segments}) => {
    selectedFunnelSegments.push({
      date,
      segments: segments.filter((segment) => {
        return selectedProps.includes(segment.prop);
      }),
    });
  });
  return selectedFunnelSegments;
}

/**
 * Use FunnelSegment to get step info to create an empty Array of FunnelSteps
 */
export function createBlankSteps(segment: FunnelSegment): Array<BasicFunnelStep | FunnelStep> {
  return segment.steps.map((step) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {custom_event, custom_event_id, event, goal, index, step_label} = step;
    const blankPValue = {insufficient_samples: true, p: 0, confidence: 0, is_interesting: false};
    return {
      avg_time: null,
      avg_time_from_start: null,
      count: 0,
      overall_conv_ratio: 0,
      step_conv_ratio: 0,
      p_value: blankPValue,
      custom_event,
      custom_event_id,
      event,
      goal,
      index,
      step_label,
    };
  });
}

/**
 * When comparing events and segmenting, $overall is present once per compared segment.
 * In such cases, we need to handle renaming on each individual segment, depth level.
 */
function renameIfOverall(propName) {
  if (propName === OVERALL_PROP) {
    return OVERALL_PROP_NAME;
  }
  return propName;
}

/**
 * Rename all segment values. Returns a FunnelSegment with renamed
 * segment.propName, which is used for display, and renamed segment.props' propName.
 */
export function renameSegments(
  segment: FunnelSegment,
  segmentNames: Array<string | string[]>,
  segmentTypes: string[],
): FunnelSegment {
  const returnedSegment = {...segment};
  if (returnedSegment.prop === OVERALL_PROP) {
    returnedSegment.propName = OVERALL_PROP_NAME;
  } else if (returnedSegment.props.length !== segmentNames.length) {
    // uneven lengths indicate a special property like $other which isn't renamed,
    // so just set propName to the props
    returnedSegment.propName = returnedSegment.props.map((seg) => renameIfOverall(seg.prop)).join(SEGMENT_SEPARATOR);
  } else {
    returnedSegment.props = zip(returnedSegment.props, segmentNames, segmentTypes).map((value) => {
      const [segmentId, name, type] = value;
      const propName = renameSegmentProperty({
        segmentValue: segmentId.prop,
        segmentName: name,
        segmentType: type,
      });
      return {prop: segmentId.prop, propName};
    });
    returnedSegment.propName = returnedSegment.props
      .map((seg) => renameIfOverall(seg.propName))
      .join(SEGMENT_SEPARATOR);
  }
  if (returnedSegment.propName === ``) {
    returnedSegment.propName = ` `;
  }
  return returnedSegment;
}

/**
 * Renames the segment property to a more friendly display name
 * based on the type of segmentation. Returns the renamed property.
 */
export function renameSegmentProperty({
  segmentValue,
  segmentName,
  segmentType,
}: {
  segmentValue: string;
  segmentName: string | string[];
  segmentType: string;
}): string {
  if (!segmentValue) {
    return ``;
  } else if (segmentType === PropertyType.String) {
    return renamePropertyValue(segmentValue, segmentName);
  }
  return segmentValue;
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getPropertyFilterLabels(step) {
  const MAX_FILTERS_FOR_DISPLAY = 3;
  const selectorParams = step.selector_params;
  if (selectorParams) {
    const operator = selectorParams.bool_op;
    let propertyFilters = selectorParams.property_filter_params_list;
    if (propertyFilters) {
      const remainingFilters = propertyFilters.length - MAX_FILTERS_FOR_DISPLAY;
      propertyFilters = propertyFilters.slice(0, MAX_FILTERS_FOR_DISPLAY);
      const transformedPropertyFilters = propertyFilters.map((filter) => {
        // Property filters of type 'object' are not currently supported
        // Since this is just using the property filter instance for generating
        // a label, we'll manually create the label we want for now
        if (filter.property.name === `$experiments`) {
          // a variant is selected
          if (filter.filter.operand) {
            return renamePropertyValue(filter.filter.operand, `$variant_id`);
          } else {
            return `Experiment is ${renamePropertyValue(filter.filter.segments[1], `$experiment_id`)}`;
          }
        } else {
          return convertSegfilterToPropertyFilter(filter).getLabel();
        }
      });
      let filterLabels = transformedPropertyFilters.join(` ${operator} `);
      if (remainingFilters > 0) {
        filterLabels = `${filterLabels} and ${remainingFilters} other ${pluralize(`condition`, remainingFilters)}`;
      }
      return {
        propertyFilterText: filterLabels,
      };
    }
  }
  return {
    propertyFilters: [],
    propertyFilterText: null,
  };
}

export function getSegments(segmentData: DateKeyedFunnelSegments[]) {
  return segmentData?.[0]?.segments;
}

export function includesAll(subset, superset) {
  return difference(subset, superset).length === 0;
}
